import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { loadStripe } from "@stripe/stripe-js";
import { Image, Header, Icon, Loader } from "semantic-ui-react";
import LoadingButton from "~/components/element/LoadingButton";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import StickyMenu from "~/components/StickyMenu";
import MediaItemStore from "~/stores/Media/MediaItemStore";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import RansomPayment from "../components/modals/RansomPayment";
import StripeConnectStore from "~/stores/StripeConnectStore";
import CustomerPaymentStore from "~/stores/CustomerPaymentStore";
import Tour from "~/models/Tour";

@observer
class RansomReviewPage extends Component {
  @observable stripeConnectStore = null;

  @observable customerPaymentStore = null;

  @observable stripePromise = null;

  @observable mediaItemStore = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Release";
    this.stripeConnectStore = new StripeConnectStore();
    this.customerPaymentStore = new CustomerPaymentStore();
    this.mediaItemStore = new MediaItemStore();

    this.state = {
      promptPayment: false,
    };
  }

  componentDidMount() {
    const { tourId, showError } = this.props;
    this.mediaItemStore
      .load(tourId)
      .then(() => this.lazyLoad())
      .catch(error => showError(error));

    // disable right click on images.
    const container = document.querySelectorAll(".tour-ransom-review .images")[0];
    container.addEventListener("contextmenu", event => {
      event.preventDefault();
      return false;
    });
  }

  onCancel() {
    this.setState({ promptPayment: false });
  }

  lazyLoad() {
    const { showError, tour, t } = this.props;

    if (tour.clientFacingStatus !== "awaiting_payment") {
      return;
    }

    this.stripeConnectStore
      .load(tour.customerId)
      .then(() => {
        const { stripeConnect } = this.stripeConnectStore;
        this.stripePromise = loadStripe(stripeConnect.providerPublishableKey);
      })
      .catch(error => {
        showError(error);
      });

    this.customerPaymentStore
      .load({ tourId: tour.tourId, awaitingPayment: true })
      .then(() => {
        const { customerPayments } = this.customerPaymentStore;

        if (customerPayments.length !== 1) {
          throw new Error(
            t("expectedOneCustomerPaymentFoundLength", { customerPaymentLength: customerPayments.length }),
          );
        }
      })
      .catch(error => showError(error));
  }

  render() {
    const { t, tour, tourId, showError, tourLayoutContentContainerRef } = this.props;
    const { promptPayment } = this.state;
    const { mediaItems, isLoading } = this.mediaItemStore;
    const lazyLoading =
      this.customerPaymentStore.isLoading || this.stripeConnectStore.isLoading || this.stripePromise === null;
    const { customerPayments } = this.customerPaymentStore;

    const customerPayment = customerPayments.length > 0 ? customerPayments[0] : null;

    return (
      <>
        <LoadingWrapper loading={isLoading} />
        <div className="tour-ransom-review">
          <StickyMenu
            contextRef={tourLayoutContentContainerRef}
            headerText={t("clientPanelRansom:reviewMedia")}
            subHeader={
              <Header.Subheader>
                {t("clientPanelRansom:awaitingPaymentOf", {
                  amountFormatted: customerPayment ? `$${customerPayment.amount}` : "",
                })}
                {!customerPayment && <Loader active inline size="tiny" />}
              </Header.Subheader>
            }
            button={
              <LoadingButton loading={lazyLoading} primary onClick={() => this.setState({ promptPayment: true })}>
                <Icon name="credit card" />
                {t("payNow")}
              </LoadingButton>
            }
          />
          {tour ? (
            <RansomPayment
              tour={tour}
              open={promptPayment}
              // url must be absolute because we'll do a full page reload.
              successRedirectUrl={`/panel/client/tours/${tourId}/download`}
              onCancel={() => this.onCancel()}
              showError={showError}
              stripeConnectStore={this.stripeConnectStore}
              customerPaymentStore={this.customerPaymentStore}
              stripePromise={this.stripePromise}
            />
          ) : null}
          <Image.Group size="medium">
            {mediaItems.map(mediaItem => (
              <Image key={`${mediaItem.uuid}_${mediaItem.type}`} src={mediaItem.imagePathWebSmall} bordered />
            ))}
          </Image.Group>
        </div>
      </>
    );
  }
}

RansomReviewPage.propTypes = {
  tourId: PropTypes.number.isRequired,
  tour: PropTypes.instanceOf(Tour).isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  t: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
};

export default withTranslation()(ErrorWrapper(TourLayoutWrapper(RansomReviewPage, TourNav.RANSOM_REVIEW)));
